import { Avatar, Tooltip, Box } from "@material-ui/core";
import useFirestore from "../../../hooks/useFirestore";
import { SIGNATURE_SYSTEM_TEMPLATE_OWNER } from "../../../@types/sharedSchema";

const WEBOT_LOGO_PATH = "/static/brand/ICON V1 512x512.png";

// Pick<> rather than the full SignatureTemplate so we can accept either the
// shared-schema shape (Firestore-style Date) or the client-side
// `@types/signatures` shape (ISO string for timestamps) — only origin-related
// fields are read here, so timestamp typing doesn't matter.
type OriginTemplate = {
  ownerUserId: string;
  organizationId?: string;
  isSystemDefault?: boolean;
  visibility?: "private" | "organization" | "public";
};

type Props = {
  template: OriginTemplate;
  /** Pixel size of the badge avatar — defaults to 20 to match small inline use. */
  size?: number;
};

/**
 * Small inline badge that shows where a signature template comes from:
 *   - Webot system default → the Webot brand icon.
 *   - Org-visible template (visibility === 'organization' AND the user is in
 *     that org) → the org logo, falling back to the first letter of the org
 *     name when no logo is set.
 *   - Anything else (private template, regardless of who owns it) → no badge.
 *
 * Note: ownership is intentionally NOT a factor. A template I shared with my
 * org should show the org badge in the marketplace just like one a coworker
 * shared. Private templates show no badge whether I own them or not — the
 * presence on /templates already implies ownership.
 *
 * Intended for use on template cards in the marketplace and on the user's
 * /signatures/templates page so a favorited or system template is clearly
 * attributed at a glance.
 */
export default function TemplateOriginBadge({ template, size = 20 }: Props) {
  const { organizations } = useFirestore();

  const isSystem =
    template.isSystemDefault === true &&
    template.ownerUserId === SIGNATURE_SYSTEM_TEMPLATE_OWNER;

  if (isSystem) {
    return (
      <Tooltip title="Webot template">
        <Avatar
          src={WEBOT_LOGO_PATH}
          alt="Webot"
          sx={{ width: size, height: size, bgcolor: "common.white" }}
        />
      </Tooltip>
    );
  }

  // Show the org badge when the template is actually shared with an org the
  // viewer belongs to. A private template that happens to have organizationId
  // set (legacy seed value from create time) gets no badge — the org doesn't
  // see it, so attributing it to the org is misleading.
  const isOrgShared =
    template.visibility === "organization" &&
    !!template.organizationId &&
    organizations.some((o) => o.id === template.organizationId);

  if (isOrgShared) {
    const org = organizations.find((o) => o.id === template.organizationId);
    const name = org?.name || "Organization";
    const logo = org?.logo;
    return (
      <Tooltip title={`Organization: ${name}`}>
        {logo ? (
          <Avatar src={logo} alt={name} sx={{ width: size, height: size }} />
        ) : (
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.5,
              bgcolor: "primary.main"
            }}
          >
            {name.slice(0, 1).toUpperCase()}
          </Avatar>
        )}
      </Tooltip>
    );
  }

  // No badge for private templates (own or otherwise) — keeps /templates
  // visually quiet for the common case while still attributing org/system
  // templates clearly.
  return <Box sx={{ width: size, height: size }} />;
}
